import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import dayjs from 'dayjs';

import CurrencyAmount from 'components/currency/CurrencyAmount';
import { paymentStatusChipSx, orderStatusChipSx } from 'components/table/tableChipStyles';
import { getTransactionDetail } from 'services/payment.service';
import { useToast } from 'contexts/ToastContext';
import { brand } from 'utils/designTokens';

export default function TransactionDetailsDialog({ open, transactionId, onClose, onOpenRefund }) {
  const { showApiError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDetail = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const res = await getTransactionDetail(id);
        if (res?.data) {
          setData(res.data);
        }
      } catch (err) {
        showApiError(err, 'Failed to load transaction details.');
      } finally {
        setLoading(false);
      }
    },
    [showApiError]
  );

  useEffect(() => {
    if (open && transactionId) {
      loadDetail(transactionId);
    } else {
      setData(null);
    }
  }, [open, transactionId, loadDetail]);

  const isRefundable = Boolean(data?.financials?.is_refundable);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountBalanceWalletOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              Transaction &amp; Payment Details
            </Typography>
          </Stack>
          {data?.order?.payment_status && (
            <Chip
              label={data.order.payment_status}
              size="small"
              sx={paymentStatusChipSx(data.order.payment_status)}
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5, pb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={36} />
          </Box>
        ) : !data ? (
          <Typography color="text.secondary" align="center" py={4}>
            No details found for this transaction.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {/* Financial Overview Cards */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Grand Total
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount value={data.financials?.grand_total} variant="h5" fontWeight={700} />
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Paid Amount
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount
                      value={data.financials?.paid_amount}
                      variant="h5"
                      fontWeight={700}
                      color="primary.main"
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Refunded Amount
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount
                      value={data.financials?.refunded_amount || 0}
                      variant="h5"
                      fontWeight={700}
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Refundable Balance
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount
                      value={data.financials?.remaining_refundable_amount || 0}
                      variant="h5"
                      fontWeight={700}
                      color="#2e7d32"
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Gateway & Order Info */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 2, borderColor: brand.border, height: '100%' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                    Stripe / Gateway Data
                  </Typography>
                  <Stack spacing={1.2} mt={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Gateway:
                      </Typography>
                      <Chip
                        label={data.transaction?.gateway || data.payment_method?.type || 'card'}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'uppercase', fontWeight: 700, height: 22 }}
                      />
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Gateway Status:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {data.transaction?.status || '—'}
                      </Typography>
                    </Stack>

                    {data.transaction?.stripe_payment_intent_id && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Payment Intent:
                        </Typography>
                        <Typography variant="caption" fontFamily="monospace">
                          {data.transaction.stripe_payment_intent_id}
                        </Typography>
                      </Stack>
                    )}

                    {data.transaction?.stripe_charge_id && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Stripe Charge:
                        </Typography>
                        <Typography variant="caption" fontFamily="monospace">
                          {data.transaction.stripe_charge_id}
                        </Typography>
                      </Stack>
                    )}

                    {data.transaction?.paid_at && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Paid At:
                        </Typography>
                        <Typography variant="caption">
                          {dayjs(data.transaction.paid_at).format('YYYY-MM-DD HH:mm:ss')}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 2, borderColor: brand.border, height: '100%' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                    Associated Order
                  </Typography>
                  <Stack spacing={1.2} mt={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Order Number:
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {data.order?.order_no || `#${data.order?.id}`}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Order Status:
                      </Typography>
                      {data.order?.order_status ? (
                        <Chip
                          label={data.order.order_status}
                          size="small"
                          sx={orderStatusChipSx(data.order.order_status)}
                        />
                      ) : (
                        '—'
                      )}
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Customer:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {data.customer?.name || '—'} ({data.customer?.mobile_number || '—'})
                      </Typography>
                    </Stack>

                    {data.store && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Store:
                        </Typography>
                        <Typography variant="body2">{data.store.name || '—'}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Refund Ledger History */}
            {Array.isArray(data.refund_history) && data.refund_history.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1}>
                  Refund Ledger ({data.refund_history.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: brand.canvas }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.refund_history.map((h, i) => (
                        <TableRow key={h.id || i}>
                          <TableCell>
                            <Chip
                              label={h.payment_status}
                              size="small"
                              sx={paymentStatusChipSx(h.payment_status)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <CurrencyAmount value={h.amount} fontWeight={600} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {h.note || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {h.timestamp ? dayjs(h.timestamp).format('YYYY-MM-DD HH:mm') : '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Webhook Events */}
            {Array.isArray(data.webhook_events) && data.webhook_events.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1}>
                  Stripe Webhook Processing Events ({data.webhook_events.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: brand.canvas }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Event Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Event ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Processed At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.webhook_events.map((e, i) => (
                        <TableRow key={e.id || i}>
                          <TableCell>
                            <Typography variant="caption" fontWeight={600}>
                              {e.event_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={e.status}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                bgcolor: e.status === 'processed' ? 'rgba(31, 91, 64, 0.1)' : 'rgba(237, 108, 2, 0.1)',
                                color: e.status === 'processed' ? brand.primary : '#e65100'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {e.event_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {e.processed_at ? dayjs(e.processed_at).format('YYYY-MM-DD HH:mm') : '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>

        {isRefundable && data && (
          <Button
            variant="contained"
            startIcon={<CurrencyExchangeIcon />}
            onClick={() => {
              onClose();
              if (onOpenRefund) onOpenRefund(data);
            }}
            sx={{
              bgcolor: brand.primary,
              '&:hover': { bgcolor: brand.primaryDark }
            }}
          >
            Issue Stripe Refund
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

TransactionDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onOpenRefund: PropTypes.func
};
